import type { ButtonHTMLAttributes, ReactNode } from 'react'

import { cx } from './cx'

/*
 | El botón primario estaba escrito catorce veces a mano, con cuatro rellenos
 | distintos (`px-4 py-2`, `px-4 py-3`, `px-5 py-3`, `px-6 py-3`), dos radios y
 | `text-white` fijo. Ese `text-white` es un fallo real, no una inconsistencia
 | estética: un estudio con la marca amarilla tenía botones ilegibles. Ahora la
 | tinta la calcula el motor de temas y llega en `accent-contrast`.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-accent-contrast hover:bg-accent-hover active:bg-accent-active',
  secondary:
    'border border-line-strong text-ink hover:bg-surface-sunken active:bg-surface-sunken',
  ghost: 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
  danger: 'bg-danger text-danger-contrast hover:brightness-95 active:brightness-90',
}

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 gap-1.5 px-3 text-label',
  md: 'h-10 gap-2 px-4 text-label',
  // 48 px. La acción principal del portal se pulsa con el pulgar en movimiento,
  // y los 44 px del mínimo táctil son el suelo, no el objetivo.
  lg: 'h-12 gap-2 px-5 text-body',
}

const BASE =
  'inline-flex select-none items-center justify-center whitespace-nowrap rounded-control ' +
  'font-medium transition-colors disabled:pointer-events-none disabled:opacity-45'

/**
 * Las clases sueltas, para un `<Link>` de React Router.
 *
 * Existe porque un botón que navega tiene que ser un `<a>` de verdad: si es un
 * `<button>` con `navigate()`, el clic central y «abrir en pestaña nueva» dejan
 * de funcionar, y en el panel de un fotógrafo abrir varios proyectos a la vez
 * es lo normal. La alternativa —un componente polimórfico con `as`— cuesta más
 * tipos de los que ahorra.
 */
export function buttonClasses(
  variant: ButtonVariant = 'primary',
  size: ButtonSize = 'md',
  extra?: string,
): string {
  // `action` la lee la regla de 44 px de index.css para punteros gruesos.
  return cx('action', BASE, VARIANTS[variant], SIZES[size], extra)
}

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Ocupa todo el ancho. En móvil es lo habitual para la acción principal. */
  block?: boolean
  /**
   * Deshabilita y sustituye el contenido por el texto de espera.
   *
   * Se pasa el texto y no un booleano a propósito: «Procesando el pago…» dice
   * bastante más que un disco girando, sobre todo con 80 ms de latencia hasta
   * Lima y una pasarela de por medio.
   */
  busy?: string | false
  children: ReactNode
  className?: string
}

export function Button({
  variant = 'primary',
  size = 'md',
  block,
  busy = false,
  disabled,
  children,
  className,
  type = 'button',
  ...rest
}: Props) {
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled === true || busy !== false}
      // Cuenta el estado a quien usa un lector de pantalla, que no ve el texto
      // cambiar a «Procesando…».
      aria-busy={busy !== false || undefined}
      className={buttonClasses(variant, size, cx(block === true && 'w-full', className))}
    >
      {busy === false ? children : busy}
    </button>
  )
}
